import { AlertType, Season, TimeSlot } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  SectionHeader,
} from '_atoms';
import { SeasonDetailsService, TicketService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DayPicker } from 'react-day-picker';
import {
  MdEvent,
  MdExpandLess,
  MdExpandMore,
  MdPeople,
  MdSchedule,
} from 'react-icons/md';

interface AddTicketsToSessionDateTimeProps {
  onSelectTimeSlot?: (timeSlot: TimeSlot) => void;
  selectedTimeSlot?: TimeSlot;
}

export const AddTicketsToSessionDateTime: React.FC<
  AddTicketsToSessionDateTimeProps
> = ({ onSelectTimeSlot, selectedTimeSlot: initialTimeSlot }) => {
  // Helper function to convert Date to local YYYY-MM-DD format
  const getLocalDateString = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper function to parse YYYY-MM-DD string as local date (not UTC)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [selectedDate, setSelectedDate] = useState<string>(
    initialTimeSlot?.date || getLocalDateString(new Date()),
  );
  const [currentSelectedTimeSlot, setCurrentSelectedTimeSlot] = useState<
    TimeSlot | undefined
  >(initialTimeSlot);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({ isOpen: true, type, title, message });
    },
    [],
  );

  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
        } else {
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, [showAlert]);

  const loadTimeSlotsForDate = useCallback(
    async (date: string) => {
      setIsLoadingTimeSlots(true);
      try {
        const responseData = (await TicketService.getTimeSlotsForDate(
          date,
        )) as unknown as TimeSlot[];

        setTimeSlots(responseData || []);
      } catch (error) {
        console.error('Error loading time slots:', error);
        showAlert(
          'error',
          'Error',
          'Failed to load skating sessions for the selected date.',
        );
        setTimeSlots([]);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    },
    [showAlert],
  );

  useEffect(() => {
    if (selectedDate) {
      loadTimeSlotsForDate(selectedDate);
    } else {
      setTimeSlots([]);
    }
  }, [selectedDate, loadTimeSlotsForDate]);

  const onSelectTimeSlotRef = useRef(onSelectTimeSlot);
  onSelectTimeSlotRef.current = onSelectTimeSlot;

  useEffect(() => {
    if (currentSelectedTimeSlot && onSelectTimeSlotRef.current) {
      onSelectTimeSlotRef.current(currentSelectedTimeSlot);
    }
  }, [currentSelectedTimeSlot]);

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  // Filter time slots to show only current hour and future sessions
  const filteredTimeSlots = useMemo(() => {
    if (!selectedDate || timeSlots.length === 0) {
      return timeSlots;
    }

    const today = getLocalDateString(new Date());

    // If selected date is not today, show all time slots
    if (selectedDate !== today) {
      return timeSlots;
    }

    // If selected date is today, filter out past time slots (keep last 2 hours)
    const now = new Date();
    const currentHour = now.getHours();

    return timeSlots.filter((timeSlot: TimeSlot) => {
      // Parse the time slot time (assuming format like "14:00" or "2:00 PM")
      const timeStr = timeSlot.timeSlot;
      const [time, period] = timeStr.split(' ');

      let slotHour: number;

      if (period) {
        // Handle AM/PM format
        const [hours] = time.split(':').map(Number);
        slotHour = hours;

        if (period === 'PM' && hours !== 12) {
          slotHour = hours + 12;
        } else if (period === 'AM' && hours === 12) {
          slotHour = 0;
        }
      } else {
        // Handle 24-hour format
        const [hours] = time.split(':').map(Number);
        slotHour = hours;
      }

      // Show sessions from 2 hours ago onwards (inclusive)
      return slotHour >= currentHour - 2;
    });
  }, [timeSlots, selectedDate]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {isLoadingInitial ? (
        <div className="flex items-center justify-center py-8 sm:py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {/* Date Picker - Collapsible */}
          <div className="w-full">
            {settings && settings.startDate && (
              <div className="space-y-2">
                {/* Collapsed Date Bar */}
                <Card className="w-full">
                  <div
                    className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <MdEvent className="text-blue-600 text-lg sm:text-xl" />
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm text-gray-500">
                          Selected Date
                        </div>
                        <div className="text-base sm:text-lg font-semibold text-gray-900">
                          {selectedDate
                            ? parseLocalDate(selectedDate).toLocaleDateString(
                                'en-US',
                                {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                },
                              )
                            : 'Select a Date'}
                        </div>
                      </div>
                    </div>
                    <div className="text-gray-400">
                      {isCalendarExpanded ? (
                        <MdExpandLess className="text-xl sm:text-2xl" />
                      ) : (
                        <MdExpandMore className="text-xl sm:text-2xl" />
                      )}
                    </div>
                  </div>
                </Card>

                {/* Season Info */}
                {settings.startDate && settings.endDate && (
                  <p className="text-xs text-center text-gray-500">
                    Season runs from{' '}
                    {new Date(settings.startDate).toLocaleDateString()} to{' '}
                    {new Date(settings.endDate).toLocaleDateString()}
                  </p>
                )}

                {/* Expanded Calendar */}
                {isCalendarExpanded && (
                  <div className="flex justify-center">
                    <Card className="w-full max-w-md">
                      <div className="p-2 sm:p-4">
                        <DayPicker
                          mode="single"
                          startMonth={
                            settings.startDate
                              ? new Date(settings.startDate)
                              : new Date()
                          }
                          selected={
                            selectedDate
                              ? parseLocalDate(selectedDate)
                              : undefined
                          }
                          onSelect={e => {
                            setSelectedDate(e ? getLocalDateString(e) : '');
                            setIsCalendarExpanded(false);
                          }}
                          modifiers={{
                            disabled: [
                              // Disable past dates (before today)
                              {
                                before: new Date(
                                  new Date().setHours(0, 0, 0, 0),
                                ),
                              },
                              // Disable dates outside season if season dates are set
                              ...(settings.startDate && settings.endDate
                                ? [
                                    { before: new Date(settings.startDate) },
                                    { after: new Date(settings.endDate) },
                                  ]
                                : []),
                            ],
                          }}
                        />
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Available Sessions */}
          <div className="w-full">
            {!selectedDate ? (
              <Card className="w-full h-full flex items-center justify-center py-6 sm:py-8">
                <EmptyState
                  title="No Date Selected"
                  description="Please select a date to view skating sessions."
                  icon={MdEvent}
                />
              </Card>
            ) : (
              <div className="w-full">
                <SectionHeader title="Available Sessions" icon={MdSchedule} />

                {isLoadingTimeSlots ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <LoadingSpinner />
                  </div>
                ) : filteredTimeSlots.length === 0 ? (
                  <EmptyState
                    title="No Skating Sessions Found"
                    description={`No skating sessions found for ${parseLocalDate(selectedDate).toLocaleDateString()}.`}
                    icon={MdSchedule}
                  />
                ) : (
                  <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4 w-full">
                    {filteredTimeSlots.map((timeSlot: any) => (
                      <div
                        key={timeSlot.id}
                        onClick={() => setCurrentSelectedTimeSlot(timeSlot)}
                        className="cursor-pointer"
                      >
                        <div
                          className={`flex w-full gap-2 sm:gap-4 justify-between border rounded-lg p-2.5 sm:p-4 transition-all duration-200 ${
                            currentSelectedTimeSlot?.id === timeSlot.id
                              ? 'bg-blue-50 border-blue-300 shadow-md'
                              : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 mr-2 sm:mr-3 rounded-full flex items-center justify-center">
                              <MdSchedule className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-sm sm:text-base">
                                {HelperMethods.formatTime12Hour(
                                  timeSlot.timeSlot,
                                )}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500">
                                Start Time
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="flex items-center justify-end space-x-1.5 sm:space-x-2">
                              <MdPeople className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                              <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                {timeSlot.maxCapacity}
                              </span>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500">
                              Available
                            </div>
                            {timeSlot.maxCapacity < 20 && (
                              <div className="text-xs text-red-600 font-medium mt-0.5 sm:mt-1">
                                Limited Availability
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};
